import { PageClassSelector } from '../../../components/layout/TeacherWorkspace';
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AppShell } from '../../../components/layout/AppShell';
import { PageHeader } from '../../../components/layout/PageHeader';
import { teacherV2NavItems } from '../../../components/layout/v2NavItems';
import { coachingApi, sectionsApi } from '../../../services/apiV2';
import { preferredTeacherSection, rememberTeacherSection } from '../../../services/teacherSection';
import { CoachingOverviewPayload, CoachingOverviewRow, Section } from '../../../types/v2';

type Filter = 'all' | 'attention' | 'ready' | 'overdue' | 'support' | 'reviews_ready' | 'on_track' | 'review_not_started' | 'review_draft' | 'review_finalized';

const reviewLabel = (status: CoachingOverviewRow['performance_review']['status']) => ({
  not_started: 'Not started',
  draft: 'Draft',
  finalized: 'Finalized',
}[status]);

export const V2CoachingPerformance: React.FC = () => {
  const [sections, setSections] = useState<Section[]>([]);
  const [sectionId, setSectionId] = useState<number | null>(null);
  useEffect(() => { rememberTeacherSection(sectionId); }, [sectionId]);
  const [cycleId, setCycleId] = useState<number | undefined>();
  const [payload, setPayload] = useState<CoachingOverviewPayload | null>(null);
  const [filter, setFilter] = useState<Filter>('attention');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingPolicy, setSavingPolicy] = useState(false);

  const savePreset = async (presetKey: string) => {
    if (!sectionId) return;
    setSavingPolicy(true);
    setError('');
    try {
      await coachingApi.savePolicy(sectionId, { preset_key: presetKey });
      const data = await coachingApi.overview(sectionId, cycleId);
      setPayload(data);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Could not update the coaching plan.');
    } finally {
      setSavingPolicy(false);
    }
  };

  useEffect(() => {
    void sectionsApi.list().then((response) => {
      const rows = (Array.isArray(response.data) ? response.data : response.data?.results || [])
        .filter((section: Section) => section.is_active !== false);
      setSections(rows);
      setSectionId((current) => current || preferredTeacherSection(rows));
      if (rows.length === 0) setLoading(false);
    }).catch(() => setError('Could not load sections.'));
  }, []);

  useEffect(() => {
    if (!sectionId) return;
    setLoading(true);
    setError('');
    void coachingApi.overview(sectionId, cycleId).then((data: CoachingOverviewPayload) => {
      setPayload(data);
    }).catch((err: any) => {
      setError(err?.response?.data?.error || 'Could not load coaching and performance records.');
    }).finally(() => setLoading(false));
  }, [sectionId, cycleId]);

  const rows = useMemo(() => (payload?.rows || []).filter((row) => {
    if (search && !row.student.name.toLowerCase().includes(search.toLowerCase())) return false;
    if (filter === 'attention') return row.attention_reasons.length > 0;
    if (filter === 'ready') return (row.coaching_check_ins || []).some((item) => item.computed_status === 'ready_for_conference');
    if (filter === 'overdue') return (row.coaching_check_ins || []).some((item) => item.computed_status === 'overdue');
    if (filter === 'support') return (row.coaching_check_ins || []).some((item) => item.support_requested);
    if (filter === 'reviews_ready') return Boolean(
      (row.midpoint_self_review_submitted && row.midpoint_review?.status !== 'finalized')
      || (row.performance_self_review_submitted && row.performance_review.status !== 'finalized')
    );
    if (filter === 'on_track') return row.attention_reasons.length === 0;
    if (filter === 'review_not_started') return row.performance_review_expected !== false && row.performance_review.status === 'not_started';
    if (filter === 'review_draft') return row.performance_review.status === 'draft';
    if (filter === 'review_finalized') return row.performance_review.status === 'finalized';
    return true;
  }), [filter, payload, search]);
  const reviewsReady = payload?.rows.filter(row => (
    (row.midpoint_self_review_submitted && row.midpoint_review?.status !== 'finalized')
    || (row.performance_self_review_submitted && row.performance_review.status !== 'finalized')
  )).length || 0;
  const summaryCards: Array<{ label: string; value: number; filter: Filter }> = payload ? [
    { label: 'Needs attention', value: payload.summary.needs_attention || payload.rows.filter((row) => row.attention_reasons.length > 0).length, filter: 'attention' },
    { label: 'Ready to meet', value: payload.summary.ready_for_conference || 0, filter: 'ready' },
    { label: 'Support requested', value: payload.summary.support_requested || 0, filter: 'support' },
    { label: 'Reviews ready', value: reviewsReady, filter: 'reviews_ready' },
  ] : [];

  return (
    <AppShell role="teacher" tone="elevate" navItems={teacherV2NavItems('coaching')} title="Coaching & Performance">
      <div className="space-y-5">
        <PageHeader
          eyebrow={payload?.selected_cycle?.title || 'Coaching'}
          title="Today’s coaching and review plan"
          subtitle="Only students needing a conversation, support, or review appear first."
          actions={payload?.policy ? <details className="relative"><summary className="cursor-pointer list-none rounded-md border border-border bg-surface px-3 py-2 text-sm font-medium text-text">Plan settings</summary><div className="absolute right-0 z-20 mt-2 w-80 rounded-xl border border-border bg-surface p-4 shadow-card"><label className="text-sm font-medium text-text">Coaching plan<select aria-label="Coaching plan" disabled={savingPolicy} className="mt-2 w-full rounded-md border border-border bg-surface px-3 py-2" value={payload.policy.preset_key} onChange={(event) => void savePreset(event.target.value)}><option value="off">Off</option><option value="grades_6_8_optional">Grades 6–8 · optional support</option><option value="grades_9_10_midpoint">Grades 9–10 · suggested midpoint</option><option value="grades_11_12_goal">Grades 11–12 · weeks 3 and 8 coaching + midpoint/final reviews</option></select></label><p className="mt-3 text-xs text-muted">Student preparation: {payload.policy.student_preparation} · cadence: {payload.policy.cadence.replace(/_/g, ' ')} · {payload.policy.requirement_level}</p>{payload.policy.preset_key === 'grades_11_12_goal' && payload.policy.cadence === 'weeks_2_4' && <button type="button" disabled={savingPolicy} onClick={() => void savePreset('grades_11_12_goal')} className="mt-3 w-full rounded-md bg-brand px-3 py-2 text-sm font-medium text-brand-foreground">Use updated 10-week cadence</button>}</div></details> : undefined}
        />

        <div className="grid gap-3 md:grid-cols-[minmax(12rem,0.8fr)_minmax(14rem,0.8fr)_minmax(16rem,1.2fr)]">
          <PageClassSelector><label className="text-sm font-medium text-text">Section
            <select className="mt-1 w-full rounded-md border border-border bg-surface px-3 py-2" value={sectionId || ''} onChange={(event) => { setCycleId(undefined); setPayload(null); setSectionId(Number(event.target.value)); }}>
              {sections.map((section) => <option key={section.id} value={section.id}>{section.name}</option>)}
            </select>
          </label></PageClassSelector>
          <label className="text-sm font-medium text-text">Course or performance block
            <select className="mt-1 w-full rounded-md border border-border bg-surface px-3 py-2" value={cycleId || payload?.selected_cycle?.id || ''} onChange={(event) => setCycleId(Number(event.target.value))}>
              {(payload?.cycles || []).map((cycle) => <option key={cycle.id} value={cycle.id}>{cycle.title} · {cycle.state}</option>)}
            </select>
          </label>
          <label className="text-sm font-medium text-text">Find student
            <input className="mt-1 w-full rounded-md border border-border bg-surface px-3 py-2" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search by name" />
          </label>
        </div>

        {payload?.selected_cycle && (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border bg-surface px-4 py-3 text-sm text-text">
            <span><span className="font-semibold">{payload.selected_cycle.title}</span> · {payload.selected_cycle.start_date} to {payload.selected_cycle.end_date}</span>
            <span className="text-xs text-muted">{payload.summary.eligible_students} students</span>
          </div>
        )}

        {error && <div role="alert" className="rounded-xl border border-danger/20 bg-danger/10 p-4 text-sm text-danger">{error}</div>}

        {payload && (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {summaryCards.map((card) => (
              <button key={card.label} type="button" onClick={() => setFilter(card.filter)} className={`rounded-xl border p-4 text-left ${filter === card.filter ? 'border-brand bg-brand/5' : 'border-border bg-surface'}`}>
                <p className="text-2xl font-semibold text-text">{card.value}</p>
                <p className="mt-1 text-xs text-muted">{card.label}</p>
              </button>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between gap-3"><h2 className="font-semibold text-text">Prioritized students</h2><span className="text-xs text-muted">Most urgent first</span></div>
        <div className="space-y-2">
          {loading && !payload ? <p className="text-sm text-muted">Loading coaching records…</p> : rows.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border bg-surface p-8 text-center text-sm text-muted">No students match this view.</div>
          ) : rows.map((row) => (
            <article key={row.student.id} className={`rounded-xl border bg-surface p-4 ${row.attention_reasons.length ? 'border-l-4 border-border border-l-warning' : 'border-border'}`}>
              <div className="grid items-center gap-4 lg:grid-cols-[minmax(14rem,1.5fr)_minmax(10rem,0.8fr)_auto]">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-semibold text-text">{row.student.name}</h3>
                    <span className="rounded-full bg-surface-alt px-2 py-0.5 text-xs text-muted">{row.student.grade_level}</span>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${row.performance_review.status === 'finalized' ? 'bg-success/10 text-success' : row.performance_review.status === 'draft' ? 'bg-warning/10 text-warning' : 'bg-surface-alt text-muted'}`}>{row.performance_review_expected === false && row.performance_review.status === 'not_started' ? 'Automatic progress summary' : reviewLabel(row.performance_review.status)}</span>
                    {row.performance_self_review_submitted && <span className="rounded-full bg-success/10 px-2 py-0.5 text-xs font-medium text-success">Self-review ready</span>}
                  </div>
                  <p className="mt-1 text-sm font-medium text-text">{row.goal?.title || (payload?.policy.goal_required ? 'No approved goal connected' : 'Lesson and assignment progress')}</p>
                  <p className="mt-1 line-clamp-2 text-xs text-muted">{row.goal?.target || (payload?.policy.goal_required ? 'Connect an approved five-week goal to this performance period.' : 'Saved work is collected automatically. A personal goal is optional.')}</p>
                </div>
                <div className="text-sm">
                  <div className="flex gap-1" aria-label={`${4 - row.evidence_gap_count} of 4 evidence stages covered`}>{(['define', 'do', 'explain', 'apply'] as const).map((stage) => <span key={stage} className={`h-1.5 flex-1 rounded-full ${row.evidence_coverage?.[stage]?.state === 'not_assessed' ? 'bg-border' : 'bg-brand'}`} />)}</div>
                  <p className="mt-2 text-xs text-muted">Evidence {4 - row.evidence_gap_count} of 4 · {(row.coaching_check_ins || []).length} check-in{(row.coaching_check_ins || []).length === 1 ? '' : 's'}</p>
                  {row.latest_instructional_check_in && <p className="mt-1 line-clamp-1 text-xs text-muted">Next: {row.latest_instructional_check_in.agreed_next_deliverable}</p>}
                </div>
                {row.midpoint_review_expected && row.midpoint_review?.status !== 'finalized'
                  ? <Link className="justify-self-start rounded-md bg-brand px-3 py-2 text-sm font-medium text-brand-foreground lg:justify-self-end" to={`/v2/teacher/coaching/sections/${payload?.section.id}/students/${row.student.id}/reviews/${payload?.selected_cycle?.id || ''}?section=${payload?.section.id}&review_kind=midpoint`}>{row.midpoint_self_review_submitted ? 'Review midpoint' : 'Prepare midpoint'}</Link>
                  : <Link className="justify-self-start rounded-md bg-brand px-3 py-2 text-sm font-medium text-brand-foreground lg:justify-self-end" to={`/v2/teacher/coaching/sections/${payload?.section.id}/students/${row.student.id}?cycle_id=${payload?.selected_cycle?.id || ''}`}>{(row.coaching_check_ins || []).some((item) => item.support_requested) ? 'View request' : (row.coaching_check_ins || []).some((item) => ['ready_for_conference', 'overdue'].includes(item.computed_status)) ? 'Open check-in' : 'Open record'}</Link>}
              </div>

              {row.attention_reasons.length > 0 && (
                <ul className="mt-3 list-disc space-y-1 border-t border-border pt-3 pl-5 text-xs text-warning">
                  {row.attention_reasons.map((reason) => <li key={reason}>{reason}</li>)}
                </ul>
              )}
            </article>
          ))}
        </div>
      </div>
    </AppShell>
  );
};
